import { Box, Grid, Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { AssetDetail } from 'views/SensorAnalysisView/SensorAnalysisView/Header/AssetDetail';
import { type Asset } from 'types/asset.types';
import { AssetSelector } from './AssetSelector';
import { getNotificationIndex } from 'views/FleetOverview/components/AssetCardsV2/utils';
import { type RuleNotifications } from 'types/notification.types';
import { useAppSelector } from 'store/hook';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const Header = ({
  enableDarkTheme,
  selectedAsset,
  latestTagData,
  hierarchyInfo,
  makeModels,
  assetBumperIds,
  setSelectedAsset,
  selectedMakeModel,
  setSelectedMakeModel,
  notifications,
}: {
  enableDarkTheme: boolean;
  selectedAsset: Asset;
  latestTagData: any;
  hierarchyInfo: any;
  makeModels: string[];
  assetBumperIds: string[];
  setSelectedAsset: any;
  selectedMakeModel: string;
  setSelectedMakeModel: any;
  notifications: RuleNotifications[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const assetHealth = useAppSelector((state) => state.assetHealthReducer);

  const notificationIndex = getNotificationIndex(notifications);

  const { assets } = useAppSelector((state) => state.assetReducer);

  return (
    <Grid container>
      <Grid item sm={12} md={6} lg={3}>
        <Box
          sx={{
            borderRadius: '5px',
            marginTop: '17px',
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
            color: palette.textPrimary,
            paddingBottom: '5px',
            display: 'flex',
            minHeight: '100px',
          }}
        >
          <AssetSelector
            makeModels={makeModels}
            enableDarkTheme={enableDarkTheme}
            selectedMakeModel={selectedMakeModel}
            setSelectedMakeModel={setSelectedMakeModel}
            assetBumperIds={assetBumperIds}
            selectedAsset={selectedAsset}
            setSelectedAsset={setSelectedAsset}
            inputWidth="130px"
            latestTagData={latestTagData}
          />
        </Box>
      </Grid>
      <Grid item sm={12} md={6} lg={3}>
        <Box
          sx={{
            borderRadius: '5px',
            marginLeft: '10px',
            marginTop: '17px',
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
            color: palette.textPrimary,
            paddingBottom: '15px',
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '5px 10px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {t('asset_health')}
            <span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 'normal',
                }}
              >
                {t('last_updated')}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  paddingLeft: '5px',
                }}
              >
                {convertUtcToDisplayTime(
                  notificationIndex.time,
                  preferredTimeDisplayMode,
                  timezone,
                  'DD/MM/YYYY h:mmA'
                )}
              </span>
            </span>
          </Typography>

          <Box
            sx={{
              width: '85%',
              textAlign: 'center',
              margin: '20px auto 0 auto',
              borderRadius: '5px',
              height: '39px',
              backgroundColor:
                assetHealth.status === 'Unhealthy'
                  ? '#FF6B6B' // Red for unhealthy
                  : assetHealth.status === 'Unknown'
                  ? '#FFA500' // Orange for unknown
                  : '#6BCB77', // Green for healthy
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography>
              {assetHealth.status === 'Unhealthy'
                ? t('vehicle_status_needs_attention')
                : assetHealth.status === 'Unknown'
                ? t('vehicle_status_status_unknown')
                : t('vehicle_status_all_systems_normal')}
            </Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item sm={12} md={12} lg={6}>
        <AssetDetail
          enableDarkTheme={enableDarkTheme}
          selectedAsset={selectedAsset}
          latestTagData={latestTagData}
          hierarchyInfo={hierarchyInfo}
          assets={assets}
        />
      </Grid>
    </Grid>
  );
};

export default Header;
